export type ModerationTargetKind = 'post' | 'comment' | 'live_message' | 'member'

export type ModerationTarget = {
  kind: ModerationTargetKind
  id: string
  authorId?: string
  authorName?: string
  body?: string
}

export type ModerationAction = 'remove_post' | 'remove_comment' | 'remove_live_message' | 'block_member' | 'unblock_member'
export type ModerationRequest =
  | { action: 'remove_post' | 'remove_comment' | 'remove_live_message'; targetId: string; reason?: string }
  | { action: 'block_member' | 'unblock_member'; memberId: string; reason?: string }

export type BlockedMember = { id: string; handle: string; display_name: string; is_active: false }
export type BlockedMembersPage = { blocked_members: BlockedMember[]; has_more: boolean; next_cursor: string | null }
export type ModerationCompletion = {
  action: ModerationAction
  target_type: ModerationTargetKind
  target_id: string
  status: 'removed' | 'blocked' | 'active'
  changed: boolean
  member?: { id: string; handle: string; display_name: string; is_active: boolean }
}

const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{1,127}$/
const ACTIONS = new Set<ModerationAction>(['remove_post', 'remove_comment', 'remove_live_message', 'block_member', 'unblock_member'])
export const MAX_MODERATION_REASON_LENGTH = 500

export class AdminModerationError extends Error {
  readonly code: string
  readonly status: number
  constructor(message: string, code = 'moderation_failed', status = 0) {
    super(message)
    this.name = 'AdminModerationError'
    this.code = code
    this.status = status
  }
}

export function removalAction(kind: ModerationTargetKind): ModerationAction | null {
  if (kind === 'post') return 'remove_post'
  if (kind === 'comment') return 'remove_comment'
  if (kind === 'live_message') return 'remove_live_message'
  return null
}

export function moderationRequest(action: ModerationAction, id: string, reason = ''): ModerationRequest {
  if (!ACTIONS.has(action) || typeof id !== 'string' || !ID_PATTERN.test(id)) throw new AdminModerationError('The moderation target is invalid.', 'invalid_target')
  if (typeof reason !== 'string') throw new AdminModerationError('The moderation reason is invalid.', 'invalid_reason')
  const cleanReason = reason.trim()
  if (cleanReason.length > MAX_MODERATION_REASON_LENGTH) {
    throw new AdminModerationError('Keep the moderation reason to 500 characters or fewer.', 'invalid_reason')
  }
  const optionalReason = cleanReason ? { reason: cleanReason } : {}
  return action === 'block_member' || action === 'unblock_member'
    ? { action, memberId: id, ...optionalReason }
    : { action, targetId: id, ...optionalReason }
}

export function moderationCopy(action: ModerationAction, name?: string) {
  const memberName = name?.trim() || 'this member'
  if (action === 'block_member') return {
    title: `Block ${memberName}?`,
    description: 'Blocking suspends this member’s Journey90 access, including sign-in, posting and live chat. Their existing content is hidden while blocked, not deleted. You can unblock them later.',
    reviewLabel: 'Review member block',
    confirmLabel: 'Confirm block member',
  }
  if (action === 'unblock_member') return {
    title: `Unblock ${memberName}?`,
    description: 'This restores the member’s Journey90 access. They will need to sign in again. Content hidden by the block becomes visible again; content removed separately is not restored.',
    reviewLabel: 'Review unblock',
    confirmLabel: 'Confirm unblock member',
  }
  const label = action === 'remove_post' ? 'post' : action === 'remove_comment' ? 'reply' : 'room message'
  return {
    title: `Remove this ${label}?`,
    description: `This ${label} will no longer be visible on Journey90. The removal is recorded for administration; it does not block the author’s account.`,
    reviewLabel: `Review ${label} removal`,
    confirmLabel: `Confirm remove ${label}`,
  }
}

async function moderationResponse(response: Response): Promise<Record<string, unknown>> {
  const payload = await response.json().catch(() => null) as Record<string, unknown> | null
  if (!response.ok) {
    throw new AdminModerationError(
      typeof payload?.message === 'string' ? payload.message : 'Journey90 could not complete that administrative request.',
      typeof payload?.error === 'string' ? payload.error : 'moderation_failed', response.status,
    )
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new AdminModerationError('The administrative response could not be verified.', 'invalid_response', response.status)
  }
  return payload
}

export async function listBlockedMembers(
  options: { after?: string | null; signal?: AbortSignal } = {},
  fetcher: typeof fetch = fetch,
): Promise<BlockedMembersPage> {
  const params = new URLSearchParams({ limit: '50' })
  if (options.after) {
    if (!ID_PATTERN.test(options.after)) throw new AdminModerationError('The member-list cursor is invalid.', 'invalid_cursor')
    params.set('after', options.after)
  }
  const payload = await moderationResponse(await fetcher(`/api/moderation?${params}`, {
    credentials: 'same-origin', cache: 'no-store', signal: options.signal,
  }))
  if (!Array.isArray(payload.blocked_members) || typeof payload.has_more !== 'boolean') {
    throw new AdminModerationError('The blocked-member list could not be verified.', 'invalid_response')
  }
  const blocked_members = payload.blocked_members.map((value: unknown): BlockedMember => {
    if (!value || typeof value !== 'object') throw new AdminModerationError('The blocked-member list could not be verified.', 'invalid_response')
    const member = value as Record<string, unknown>
    if (typeof member.id !== 'string' || !ID_PATTERN.test(member.id) || typeof member.handle !== 'string'
      || typeof member.display_name !== 'string' || member.is_active !== false) {
      throw new AdminModerationError('The blocked-member list could not be verified.', 'invalid_response')
    }
    return { id: member.id, handle: member.handle, display_name: member.display_name, is_active: false }
  })
  const next_cursor = typeof payload.next_cursor === 'string' && ID_PATTERN.test(payload.next_cursor) ? payload.next_cursor : null
  if (payload.has_more && !next_cursor) throw new AdminModerationError('The next member-list page could not be verified.', 'invalid_response')
  return { blocked_members, has_more: payload.has_more, next_cursor }
}

export async function performAdminModeration(
  request: ModerationRequest, signal?: AbortSignal, fetcher: typeof fetch = fetch,
): Promise<ModerationCompletion> {
  const payload = await moderationResponse(await fetcher('/api/moderation', {
    method: 'POST', credentials: 'same-origin', cache: 'no-store',
    headers: { 'content-type': 'application/json' }, body: JSON.stringify(request), signal,
  }))
  const id = 'memberId' in request ? request.memberId : request.targetId
  const target_type = request.action === 'block_member' || request.action === 'unblock_member'
    ? 'member' : request.action === 'remove_post' ? 'post' : request.action === 'remove_comment' ? 'comment' : 'live_message'
  const status = request.action === 'block_member' ? 'blocked' : request.action === 'unblock_member' ? 'active' : 'removed'
  if (payload.action !== request.action || payload.target_id !== id || payload.target_type !== target_type
    || payload.status !== status || typeof payload.changed !== 'boolean') {
    throw new AdminModerationError('The administrative result could not be verified. Refresh before trying again.', 'invalid_response')
  }
  const result: ModerationCompletion = {
    action: request.action, target_type, target_id: id, status, changed: payload.changed,
  }
  if (payload.member && typeof payload.member === 'object') {
    const member = payload.member as Record<string, unknown>
    if (member.id === id && typeof member.handle === 'string' && typeof member.display_name === 'string'
      && typeof member.is_active === 'boolean') {
      result.member = { id, handle: member.handle, display_name: member.display_name, is_active: member.is_active }
    }
  }
  return result
}
